import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Sparkles, Zap, AlertCircle, Loader2 } from 'lucide-react';
import { GUSTAFTA_LINKS, getGustaftaUrl } from '@/integrations/gustafta';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const STREAM_IDLE_TIMEOUT_MS = 45000;

const GUSTAFTA_ACTIONS = GUSTAFTA_LINKS.map(link => ({
  label: `${link.icon} ${link.title}`,
  href: getGustaftaUrl(link.path),
  q: `Saya ingin membuka ${link.title}.`,
}));

const GUSTAFTA_TERMS = [
  'gustafta',
  'cek kesiapan sbu',
  'bedah dokumen',
  'klinika',
  'klinik konsultasi',
  'peluang tender',
  'brain project',
  'ruang proyek',
];

function getGustaftaActions(text) {
  const normalized = text.toLowerCase();
  return GUSTAFTA_TERMS.some(term => normalized.includes(term))
    ? GUSTAFTA_ACTIONS
    : [];
}

// ── Local fallback KB (used only when backend is unreachable) ────────────────
const KB = [
  {
    id: 'greeting',
    keywords: ['halo', 'hai', 'hi', 'hello', 'selamat', 'pagi', 'siang', 'sore', 'malam', 'hey', 'test', 'coba'],
    phrases: ['selamat pagi', 'selamat siang', 'selamat sore', 'selamat malam'],
    reply: `👋 Halo! Saya AI Assistant RancangBangun.\n\nSaya siap membantu Anda menjelajahi:\n• 🔎 Marketplace & pencarian mitra konstruksi\n• 💰 RAB dan estimasi biaya\n• 📊 Monitoring proyek, milestone, dan laporan lapangan\n• ✅ Profil serta verifikasi LPJK dan SBU\n• ⚖️ Bidding, SPK, dan kontrak digital\n• 🛡️ Jaminan, QC, dan pembayaran termin\n\nAnda siapa? Owner/Developer atau Kontraktor?`,
    actions: [
      { label: '🔎 Cari Kontraktor', q: 'Bagaimana cara mencari kontraktor?' },
      { label: '💰 RAB & Estimasi', q: 'Apa yang bisa dilakukan RAB Calculator?' },
      { label: '📊 Monitoring Proyek', q: 'Bagaimana cara memantau progress proyek?' },
      { label: '🛡️ Jaminan & QC', q: 'Bagaimana jaminan dan QC proyek bekerja?' },
      { label: '🔗 Fitur Gustafta', q: 'Apa saja fitur Gustafta yang melengkapi RancangBangun?' },
    ]
  },
  {
    id: 'intro',
    keywords: ['rancangbangun', 'platform', 'aplikasi', 'apa', 'tentang', 'ini', 'visi', 'misi', 'tujuan'],
    phrases: ['apa itu', 'apa ini', 'rancang bangun', 'platform ini', 'tentang platform'],
    reply: `🏗️ **RancangBangun** adalah platform konstruksi terintegrasi yang menghubungkan:\n\n👔 Owner/Developer — posting proyek dan mencari mitra\n🏗️ Kontraktor — menampilkan profil, mengikuti bidding, dan mengelola proyek\n🏭 Supplier — memperkenalkan material dan melayani kebutuhan proyek\n\nFitur utamanya mencakup Marketplace, RAB, Bidding & SPK, monitoring proyek, Blueprint, Smart BOM, Gantt, Budget, Invoice, Documents, serta Jaminan & QC.`,
    actions: [
      { label: '🎯 Semua Fitur', q: 'Tampilkan semua fitur platform' },
    ]
  },
  {
    id: 'rab',
    keywords: ['rab', 'rencana', 'anggaran', 'biaya', 'budget', 'estimasi', 'hitung', 'kalkulasi'],
    phrases: ['rencana anggaran biaya', 'cara hitung', 'hitung rab', 'estimasi biaya'],
    reply: `💰 **RAB Calculator** tersedia di menu sidebar.\n\n• Menyusun estimasi material, upah, dan alat\n• Memakai template pekerjaan berdasarkan klasifikasi LPJK\n• Menyajikan breakdown pekerjaan dalam struktur WBS\n• Mengekspor RAB ke PDF untuk kebutuhan tender`,
    actions: [{ label: '📊 Buka RAB Calculator', q: 'Cara menggunakan RAB Calculator' }]
  },
  {
    id: 'features',
    keywords: ['fitur', 'modul', 'menu', 'layanan', 'semua', 'daftar'],
    phrases: ['fitur apa saja', 'semua fitur', 'daftar fitur', 'modul apa'],
    reply: `🎯 **Fitur RancangBangun:**\n\n🔎 Marketplace dan Direktori Kontraktor\n💰 RAB Calculator dan estimasi biaya\n⚖️ Bidding & SPK serta kontrak digital\n📊 Proyek Saya, Gantt, Budget, dan Site Control\n📐 Blueprint dan Smart BOM\n🛡️ Jaminan & QC, FAT, Safety, dan Maintenance\n💼 Invoice dan Documents\n🤝 Stakeholder serta Client Portal`,
    actions: []
  },
  {
    id: 'monitoring',
    keywords: ['monitoring', 'monitor', 'progress', 'progres', 'milestone', 'lapangan', 'gantt', 'proyek'],
    phrases: ['monitoring proyek', 'progress proyek', 'pantau proyek', 'laporan lapangan'],
    reply: `📊 **Monitoring Proyek** membantu Anda melihat perkembangan pekerjaan dalam satu alur.\n\n• Kelola proyek melalui **Proyek Saya**\n• Susun timeline dan milestone dengan **Gantt**\n• Catat laporan lapangan melalui **Site Control**\n• Pantau anggaran melalui **Budget Tracker**\n\nMulai dari menu yang sesuai di sidebar aplikasi.`,
    actions: [{ label: '📈 Buka Proyek Saya', q: 'Cara menggunakan Proyek Saya dan Gantt?' }]
  },
  {
    id: 'matching',
    keywords: ['cari', 'kontraktor', 'mitra', 'partner', 'marketplace', 'supplier', 'vendor'],
    phrases: ['cari kontraktor', 'mencari kontraktor', 'cari proyek', 'direktori kontraktor'],
    reply: `🔎 **Marketplace & Direktori Kontraktor** membantu mempertemukan kebutuhan proyek dengan profil mitra yang relevan.\n\n• Telusuri proyek atau kontraktor sesuai kebutuhan\n• Periksa profil, pengalaman, dan dokumen yang tersedia\n• Gunakan informasi LPJK/SBU sebagai bagian dari proses verifikasi\n• Bandingkan penawaran sebelum memilih mitra\n\nBuka **Marketplace** atau **Kontraktor** di sidebar.`,
    actions: [{ label: '🏗️ Buka Marketplace', q: 'Cara menggunakan Marketplace RancangBangun?' }]
  },
  {
    id: 'bidding',
    keywords: ['bidding', 'tender', 'spk', 'kontrak', 'penawaran', 'proposal'],
    phrases: ['proses bidding', 'ikut tender', 'kontrak digital', 'bidding dan tender'],
    reply: `⚖️ **Bidding & SPK** membantu mengatur proses penawaran dan kontrak digital.\n\n• Owner mempublikasikan kebutuhan proyek\n• Kontraktor mengirimkan proposal dan RAB\n• Penawaran dapat dibandingkan secara lebih terstruktur\n• Hasil kesepakatan dilanjutkan ke SPK dan kontrak digital\n\nBuka **Bidding & SPK** di sidebar.`,
    actions: [{ label: '⚖️ Pelajari Bidding', q: 'Bagaimana alur bidding dan kontrak digital?' }]
  },
  {
    id: 'protection',
    keywords: ['escrow', 'jaminan', 'qc', 'kualitas', 'termin', 'pembayaran', 'keamanan'],
    phrases: ['jaminan proyek', 'pembayaran termin', 'quality control', 'keamanan pembayaran'],
    reply: `🛡️ **Jaminan & QC** membantu menjaga proses proyek tetap terdokumentasi.\n\n• Atur pembayaran termin berdasarkan milestone\n• Gunakan escrow sebagai bagian dari mekanisme pembayaran\n• Catat pemeriksaan mutu dan temuan pekerjaan\n• Kelola FAT, Safety, dan Maintenance sesuai kebutuhan\n\nBuka **Jaminan & QC** di sidebar.`,
    actions: [{ label: '🛡️ Buka Jaminan & QC', q: 'Apa saja yang tersedia di Jaminan dan QC?' }]
  },
  {
    id: 'verification',
    keywords: ['lpjk', 'sbu', 'verifikasi', 'verified', 'sertifikat', 'dokumen'],
    phrases: ['verifikasi lpjk', 'sertifikat lpjk', 'profil kontraktor'],
    reply: `✅ **Verifikasi LPJK & SBU** membantu menampilkan informasi legalitas dan kompetensi mitra secara lebih jelas.\n\nPeriksa profil kontraktor, klasifikasi yang tersedia, sertifikat, pengalaman, dan dokumen pendukung sebelum mengambil keputusan.`,
    actions: [{ label: '👥 Buka Kontraktor', q: 'Bagaimana melihat profil kontraktor?' }]
  },
  {
    id: 'gustafta',
    keywords: ['gustafta', 'skk', 'bedah', 'brain', 'klinika', 'konsultasi', 'tender', 'ruang', 'proyek', 'sbu'],
    phrases: ['platform gustafta', 'gustafta dan rancangbangun', 'klinik konsultasi', 'klinika ai', 'peluang tender', 'brain project', 'ruang proyek', 'cek kesiapan sbu'],
    reply: `🔗 **Gustafta** adalah platform eksternal yang dapat melengkapi alur RancangBangun.\n\n• **Cek Kesiapan SBU** mendukung persiapan legalitas dan verifikasi mitra\n• **Bedah Dokumen** membantu membaca dokumen tender dan menemukan risiko\n• **Klinika AI** menjadi ruang konsultasi teknis dan pengetahuan konstruksi\n• **Peluang Tender** melengkapi proses pencarian dan persiapan penawaran\n• **Brain Project** membantu memberi konteks untuk supervisi dan analisis proyek\n• **Ruang Proyek** melengkapi pengelolaan dokumen serta perkembangan pekerjaan\n\nRancangBangun tetap menjadi ruang utama untuk Marketplace, RAB, Bidding & SPK, Proyek Saya, Jaminan & QC, dan pengelolaan proyek. Gustafta dibuka sebagai layanan terpisah di tab baru.`,
    actions: GUSTAFTA_ACTIONS
  },
  {
    id: 'default',
    keywords: [],
    phrases: [],
    reply: `Maaf, koneksi ke AI Assistant sedang tidak tersedia. Silakan coba beberapa saat lagi atau hubungi support@rancangbangun.id 🙏`,
    actions: []
  }
];

function tokenize(text) {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length > 1);
}

function localFallback(text) {
  const tokens = tokenize(text);
  const textLow = text.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const entry of KB) {
    if (entry.id === 'default') continue;
    let s = entry.keywords.filter(kw => tokens.includes(kw)).length;
    s += entry.phrases.filter(ph => textLow.includes(ph)).length * 3;
    if (s > bestScore) { bestScore = s; best = entry; }
  }
  return bestScore >= 1 ? best : KB.find(e => e.id === 'default');
}

// ── Text formatter ─────────────────────────────────────────────────────────────
function formatText(text) {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const formatted = parts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
    return <span key={i}>{formatted}{i < lines.length - 1 && <br />}</span>;
  });
}

const SUGGESTIONS = [
  'Cari kontraktor',
  'RAB & estimasi biaya',
  'Monitoring proyek',
  'Jaminan & QC',
  'Gustafta',
  'Klinika AI',
  'Brain Project',
  'Fitur platform',
];

function getApiErrorMessage(payload, status) {
  const detail = payload?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (payload?.message) return payload.message;
  return `HTTP ${status}`;
}

async function consumeSseStream(response, onEvent) {
  if (!response.body) {
    throw new Error('Respons streaming tidak tersedia dari server.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const dispatch = (rawEvent) => {
    const data = rawEvent
      .split(/\r?\n/)
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trimStart())
      .join('\n');
    if (!data) return;
    onEvent(JSON.parse(data));
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

    const events = buffer.split(/\r?\n\r?\n/);
    buffer = events.pop() || '';
    events.forEach(dispatch);

    if (done) break;
  }

  if (buffer.trim()) dispatch(buffer);
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function ChatbotAgentic({ isOpen, setIsOpen }) {
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: `👋 Halo! Saya AI Assistant **RancangBangun**.\n\nTanyakan apa saja tentang platform ini — cara kerja, fitur, proses tender, escrow, RAB, atau topik konstruksi lainnya!`,
      actions: [
        { label: '🔎 Cari Kontraktor', q: 'Bagaimana cara mencari kontraktor?' },
        { label: '💰 RAB & Estimasi', q: 'Apa yang bisa dilakukan RAB Calculator?' },
        { label: '📊 Monitoring Proyek', q: 'Bagaimana cara memantau progress proyek?' },
        { label: '🛡️ Jaminan & QC', q: 'Bagaimana jaminan dan QC proyek bekerja?' },
        { label: '🔗 Fitur Gustafta', q: 'Apa saja fitur Gustafta yang melengkapi RancangBangun?' },
      ]
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState(null); // null | 'active' | 'inactive'
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const messageIdRef = useRef(0);

  // Check AI status on mount
  useEffect(() => {
    fetch(`${API_BASE}/chatbot/status`)
      .then(r => r.json())
      .then(data => setAiStatus(data.ai_powered ? 'active' : 'inactive'))
      .catch(() => setAiStatus('inactive'));
  }, []);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Build conversation history for OpenAI context (last 10 turns)
  const buildHistory = (currentMessages) => {
    return currentMessages
      .slice(-10)
      .filter(m => m.type === 'user' || m.type === 'bot')
      .map(m => ({ role: m.type === 'user' ? 'user' : 'assistant', content: m.text }));
  };

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setInput('');

    const userMsg = { type: 'user', text: trimmed };
    const streamMessageId = `stream-${++messageIdRef.current}`;
    setMessages(prev => {
      const next = [...prev, userMsg];
      next.push({
        id: streamMessageId,
        type: 'bot',
        text: '',
        actions: [],
        isStreaming: true,
      });
      return next;
    });
    setLoading(true);
    let streamedText = '';
    let streamTimedOut = false;
    const controller = new AbortController();
    let timeoutId;
    const resetStreamTimeout = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        streamTimedOut = true;
        controller.abort();
      }, STREAM_IDLE_TIMEOUT_MS);
    };
    resetStreamTimeout();

    try {
      const historySnapshot = buildHistory([...messages, userMsg]);

      const res = await fetch(`${API_BASE}/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          message: trimmed,
          conversation_history: historySnapshot,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 429) {
          const retryAfter = Number.parseInt(
            res.headers.get('Retry-After') ||
              err.detail?.retry_after_seconds ||
              '',
            10
          );
          const waitMessage = Number.isFinite(retryAfter) && retryAfter > 0
            ? ` Coba lagi dalam sekitar ${retryAfter} detik.`
            : ' Coba lagi beberapa saat lagi.';
          const rateLimitError = new Error(
            `${getApiErrorMessage(err, res.status)}${waitMessage}`
          );
          rateLimitError.status = 429;
          throw rateLimitError;
        }
        throw new Error(getApiErrorMessage(err, res.status));
      }

      let receivedDone = false;
      await consumeSseStream(res, event => {
        resetStreamTimeout();

        if (event.type === 'start') {
          setMessages(prev => prev.map(msg => (
            msg.id === streamMessageId
              ? { ...msg, aiPowered: event.ai_powered }
              : msg
          )));
          return;
        }

        if (event.type === 'chunk') {
          streamedText += event.content || '';
          setMessages(prev => prev.map(msg => (
            msg.id === streamMessageId
              ? { ...msg, text: streamedText }
              : msg
          )));
          return;
        }

        if (event.type === 'done') {
          receivedDone = true;
          const response = event.response || {};
          setMessages(prev => prev.map(msg => (
            msg.id === streamMessageId
              ? {
                  ...msg,
                  text: streamedText || response.bot_response || '',
                  actions: getGustaftaActions(trimmed),
                  aiPowered: response.ai_powered,
                  isStreaming: false,
                }
              : msg
          )));
          return;
        }

        if (event.type === 'error') {
          const streamError = new Error(
            event.message || 'Respons AI gagal dikirim.'
          );
          streamError.isServerStreamError = true;
          throw streamError;
        }
      });

      if (!receivedDone) {
        throw new Error('Koneksi ke AI terputus sebelum respons selesai.');
      }
    } catch (err) {
      const removeStreamingMessage = prev => (
        prev.filter(msg => msg.id !== streamMessageId)
      );

      // Keep partial text visible when the connection fails mid-response.
      if (streamedText) {
        setMessages(prev => prev.map(msg => (
          msg.id === streamMessageId
            ? {
                ...msg,
                text: `${streamedText}\n\n⚠️ ${err.message}`,
                isStreaming: false,
                isError: true,
              }
            : msg
        )));
        return;
      }

      // Rate limiting is intentional and must be visible; do not hide it
      // behind the local KB fallback.
      if (err.status === 429) {
        setMessages(prev => [
          ...removeStreamingMessage(prev),
          {
            type: 'bot',
            text: `⏳ ${err.message}`,
            actions: [],
            isError: true,
          },
        ]);
        return;
      }

      // An SSE error is an application response from the server, not a lost
      // network connection, so its actionable message must remain visible.
      if (err.isServerStreamError) {
        setMessages(prev => [
          ...removeStreamingMessage(prev),
          {
            type: 'bot',
            text: `⚠️ ${err.message}`,
            actions: [],
            isError: true,
          },
        ]);
        return;
      }

      if (streamTimedOut) {
        setMessages(prev => [
          ...removeStreamingMessage(prev),
          {
            type: 'bot',
            text: '⚠️ Respons AI berhenti terlalu lama. Silakan coba lagi.',
            actions: [],
            isError: true,
          },
        ]);
        return;
      }

      // Network error, timeout, or server error → local KB fallback
      const fallback = localFallback(trimmed);
      const isKeyError = err.message && (
        err.message.toLowerCase().includes('api_key') ||
        err.message.toLowerCase().includes('tidak valid') ||
        err.message.toLowerCase().includes('dikonfigurasi')
      );

      setMessages(prev => [
        ...removeStreamingMessage(prev),
        {
          type: 'bot',
          text: isKeyError
            ? `⚠️ ${err.message}`
            : fallback.reply,
          actions: isKeyError ? [] : (fallback.actions || []),
          isError: isKeyError,
        },
      ]);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300"
        style={{ zIndex: 10000 }}
        title="Asisten fitur RancangBangun"
        aria-label={isOpen ? 'Tutup asisten RancangBangun' : 'Buka asisten RancangBangun'}
        aria-expanded={isOpen}
      >
        {isOpen
          ? <X className="w-6 h-6" />
          : <div className="relative">
              <MessageCircle className="w-6 h-6" />
              <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-yellow-300" />
            </div>
        }
      </button>

      {isOpen && (
        <div
          className="fixed bottom-20 right-3 sm:bottom-24 sm:right-6 bg-white rounded-2xl shadow-2xl flex flex-col border-2 border-orange-200 overflow-hidden"
          style={{
            zIndex: 10000,
            width: 'min(400px, calc(100vw - 24px))',
            height: 'min(580px, calc(100vh - 104px))',
          }}
          role="dialog"
          aria-label="Asisten fitur RancangBangun"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 rounded-t-2xl flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm">Asisten RancangBangun</div>
              <div className="text-xs text-orange-100 flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {aiStatus === 'active' ? 'Panduan fitur • AI aktif' : 'Panduan fitur • Mode lokal'}
              </div>
            </div>
            {/* AI status indicator */}
            {aiStatus === 'inactive' && (
              <div title="AI tidak aktif — OPENAI_API_KEY belum dikonfigurasi" className="p-1">
                <AlertCircle className="w-4 h-4 text-yellow-200" />
              </div>
            )}
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* AI inactive banner */}
          {aiStatus === 'inactive' && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-3 py-2 text-xs text-yellow-700 flex items-center gap-1.5 flex-shrink-0">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Mode panduan lokal — informasi fitur tetap tersedia
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-orange-50/30 to-white">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] px-4 py-3 rounded-2xl text-sm leading-relaxed
                  ${msg.type === 'user'
                    ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white'
                    : msg.isError
                      ? 'bg-red-50 border border-red-200 text-red-700 shadow-sm'
                      : 'bg-white border border-orange-100 text-slate-800 shadow-sm'}`}
                >
                  {msg.aiPowered && (
                    <div className="flex items-center gap-1 text-xs text-orange-400 mb-1.5">
                      <Sparkles className="w-3 h-3" /> AI
                    </div>
                  )}
                  {msg.text
                    ? formatText(msg.text)
                    : msg.isStreaming
                      ? <span className="text-slate-400">Mengetik...</span>
                      : null}
                  {msg.isStreaming && (
                    <span className="ml-1 text-orange-400 animate-pulse" aria-label="Respons sedang diketik">
                      ▌
                    </span>
                  )}
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-2.5 space-y-1.5">
                      {msg.actions.map((a, j) => (
                        a.href ? (
                        <a
                          key={j}
                          href={a.href}
                          target="_blank"
                          rel="noreferrer"
                          className="w-full bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 text-xs py-1.5 px-3 rounded-lg transition-colors font-medium text-left disabled:opacity-50"
                        >
                          {a.label}
                        </a>
                        ) : (
                        <button
                          key={j}
                          onClick={() => sendMessage(a.q)}
                          disabled={loading}
                          className="w-full bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 text-xs py-1.5 px-3 rounded-lg transition-colors font-medium text-left disabled:opacity-50"
                        >
                          {a.label}
                        </button>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div ref={endRef} />
          </div>

          {/* Suggestion chips */}
          <div className="px-3 py-2 bg-orange-50 border-t border-orange-100 flex-shrink-0">
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s)}
                  disabled={loading}
                  className="text-xs bg-white border border-orange-200 text-orange-700 px-2.5 py-1 rounded-full hover:bg-orange-100 transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-3 border-t border-orange-100 bg-white rounded-b-2xl flex-shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                disabled={loading}
                placeholder={loading ? 'Menunggu respons...' : 'Tanyakan fitur atau alur proyek...'}
                className="flex-1 px-3 py-2.5 border border-orange-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white p-2.5 rounded-xl transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
